import { lazy, Suspense, type ComponentType } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import Home from "./pages/Home";

/**
 * Vite content-hashes each route's JS chunk, so a tab left open across a
 * deploy gets a 404 importing its old chunk filename once that build's
 * output is replaced -- an unhandled failure that otherwise trips the
 * top-level ErrorBoundary with a generic "unexpected error" screen. Retry
 * once via a full reload (which re-fetches the current index.html/asset
 * manifest) instead, since that's really just a stale tab, not a bug.
 */
function lazyWithChunkRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    const RELOAD_FLAG = "chunk-load-retry";
    const isBrowser = typeof sessionStorage !== "undefined";
    try {
      const mod = await factory();
      if (isBrowser) sessionStorage.removeItem(RELOAD_FLAG);
      return mod;
    } catch (error) {
      if (!isBrowser) throw error;
      if (sessionStorage.getItem(RELOAD_FLAG) === "true") {
        throw error;
      }
      sessionStorage.setItem(RELOAD_FLAG, "true");
      window.location.reload();
      // Reload is already in flight; never resolve so React doesn't render
      // a broken component in the moment before the page actually unloads.
      return new Promise<{ default: T }>(() => {});
    }
  });
}

const About = lazyWithChunkRetry(() => import("./pages/About"));
const AdminAnalytics = lazyWithChunkRetry(() => import("./pages/AdminAnalytics"));
const Capabilities = lazyWithChunkRetry(() => import("./pages/Capabilities"));
const Commercial = lazyWithChunkRetry(() => import("./pages/Commercial"));
const ClaimFunnel = lazyWithChunkRetry(() => import("./pages/ClaimFunnel"));
const Contact = lazyWithChunkRetry(() => import("./pages/Contact"));
const CrewCabBrushTruck = lazyWithChunkRetry(() => import("./pages/CrewCabBrushTruck"));
const FederalContracting = lazyWithChunkRetry(() => import("./pages/FederalContracting"));
const FederalSignalLaunch = lazyWithChunkRetry(() => import("./pages/FederalSignalLaunch"));
const FireBrushTrucks = lazyWithChunkRetry(() => import("./pages/FireBrushTrucks"));
const Gallery = lazyWithChunkRetry(() => import("./pages/Gallery"));
const K9Consult = lazyWithChunkRetry(() => import("./pages/K9Consult"));
const LawEnforcement = lazyWithChunkRetry(() => import("./pages/LawEnforcement"));
const MunicipalFleetVehicleProtection = lazyWithChunkRetry(() => import("./pages/MunicipalFleetVehicleProtection"));
const NotFound = lazyWithChunkRetry(() => import("./pages/NotFound"));
const Privacy = lazyWithChunkRetry(() => import("./pages/Privacy"));
const Accessibility = lazyWithChunkRetry(() => import("./pages/Accessibility"));
const Quote = lazyWithChunkRetry(() => import("./pages/Quote"));
const Resources = lazyWithChunkRetry(() => import("./pages/Resources"));
const Terms = lazyWithChunkRetry(() => import("./pages/Terms"));
const SupplyStore = lazyWithChunkRetry(() => import("./pages/SupplyStore"));
const VehicleConfigurator = lazyWithChunkRetry(() => import("./pages/VehicleConfigurator"));

function RouteFallback() {
  return (
    <div
      className="min-h-[50vh] bg-[var(--tfrs-navy)]"
      role="status"
      aria-label="Loading page"
    />
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  // The /claim short funnel is a landing flow: no site navbar and no site
  // footer (it renders its own minimal footer).
  if (location === "/claim" || location.startsWith("/claim/")) {
    return <main id="main-content">{children}</main>;
  }
  return (
    <div className="min-h-screen flex flex-col">
      {/*
       * Skip-navigation link — WCAG 2.4.1 (Level A)
       * Visually hidden until focused; lets keyboard users bypass the navbar.
       */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-[#c00a14] focus:text-white focus:font-eyebrow focus:text-sm focus:tracking-widest focus:uppercase focus:outline-none"
      >
        Skip to main content
      </a>
      {/* display:contents keeps <header> as a landmark for assistive tech
          without adding a layout box between Navbar and the flex column —
          an extra box here breaks Navbar's own `sticky top-0` positioning. */}
      <header className="contents">
        <Navbar />
      </header>
      <main id="main-content" className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <Layout>
      <ScrollToTop />
      <Suspense fallback={<RouteFallback />}>
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/law-enforcement" component={LawEnforcement} />
          <Route path="/fire-brush-trucks" component={FireBrushTrucks} />
          <Route path="/crew-cab-brush-truck" component={CrewCabBrushTruck} />
          <Route path="/k9-consult" component={K9Consult} />
          <Route path="/federal-contracting" component={FederalContracting} />
          <Route path="/federal-signal-launch" component={FederalSignalLaunch} />
          <Route path="/claim/:step?" component={ClaimFunnel} />
          <Route path="/commercial" component={Commercial} />
          <Route path="/municipal-fleet-vehicle-protection" component={MunicipalFleetVehicleProtection} />
          <Route path="/about" component={About} />
          <Route path="/contact" component={Contact} />
          <Route path="/quote" component={Quote} />
          <Route path="/gallery" component={Gallery} />
          <Route path="/resources" component={Resources} />
          <Route path="/configure" component={VehicleConfigurator} />
          <Route path="/supply-store" component={SupplyStore} />
          <Route path="/capabilities" component={Capabilities} />
          <Route path="/privacy" component={Privacy} />
          <Route path="/terms" component={Terms} />
          <Route path="/accessibility" component={Accessibility} />
          <Route path="/admin/analytics" component={AdminAnalytics} />

          <Route path="/404" component={NotFound} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </Layout>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
